import { Loader2 } from "lucide-react";
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive:
                    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline:
                    "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                secondary:
                    "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                none: "bg-transparent"
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
            },
            loaderPosition: {
                left: "flex-row",
                right: "flex-row",
                center: "flex-col",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
            loaderPosition: "right",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
    isLoading?: boolean
    loaderPosition?: 'left' | 'right' | 'center'
}

const ButtonWithLoader = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        className,
        variant,
        size,
        asChild = false,
        isLoading = false,
        loaderPosition = 'right',
        children,
        ...props
    }, ref) => {
        const Comp = asChild ? Slot : "button"

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, loaderPosition, className }))}
                ref={ref}
                disabled={isLoading || props.disabled}
                {...props}
            >
                {isLoading && loaderPosition === 'left' && (
                    <Loader2 className="animate-spin" />
                )}
                {isLoading && loaderPosition === 'center' ? (
                    <Loader2 className="animate-spin" />
                ) : (
                    children
                )}
                {isLoading && loaderPosition === 'right' && (
                    <Loader2 className="animate-spin" />
                )}
            </Comp>
        )
    }
)
ButtonWithLoader.displayName = "Button"

export { ButtonWithLoader, buttonVariants }